import { useEffect, useMemo, useState } from 'react';
import { darkmodeMediaMatch, mediaQueryDesktop, Orientation, mediaQueryOrientationLandscape, UiContext, Provider, isInIframe, isTeams, isInModelDrivenAppContext } from './uiCtx';
import { getCombinedDarkModeState } from '../../../utils/powerAppThemeDetector';

export const UIContext = ({ children }: { children?: React.ReactNode }) => {
    const [isDarkmode] = useState(() => {
        if (isInModelDrivenAppContext) {
            // Model-driven Power Apps default to light mode regardless of browser/OS settings,
            // so we mirror that here. Theme is read once on load.
            return false;
        }
        return getCombinedDarkModeState(darkmodeMediaMatch.matches);
    });
    const [isInDesktopMode, setIsInDesktopMode] = useState(() => mediaQueryDesktop.matches);
    const layout = useMemo(() => (isInDesktopMode ? 'desktop' : 'phone'), [isInDesktopMode]);
    const [orientation, setOrientation] = useState<Orientation>(() => (mediaQueryOrientationLandscape.matches ? 'landscape' : 'portrait'));
    const [showNavbar, setShowNavbar] = useState(!(isInIframe || isTeams));

    useEffect(() => {
        const handleOrientationChange = (e: MediaQueryListEvent) => {
            setOrientation(e.matches ? 'landscape' : 'portrait');
        };
        mediaQueryOrientationLandscape.addEventListener('change', handleOrientationChange);
        return () => {
            mediaQueryOrientationLandscape.removeEventListener('change', handleOrientationChange);
        };
    }, []);

    useEffect(() => {
        const handleLayoutChange = (e: MediaQueryListEvent) => {
            setIsInDesktopMode(e.matches);
        };
        mediaQueryDesktop.addEventListener('change', handleLayoutChange);
        return () => {
            mediaQueryDesktop.removeEventListener('change', handleLayoutChange);
        };
    }, []);

    useEffect(() => {
        // needed to write css that only apply to darkmode & worked for the Layer/Callout's too.
        if (isDarkmode === undefined) {
            return;
        }

        const bodyClasslist = document.body.classList;
        if (!bodyClasslist) {
            return;
        }

        if (isDarkmode) {
            if (!bodyClasslist.contains('isDarkmode')) {
                bodyClasslist.add('isDarkmode');
            }
        } else if (bodyClasslist.contains('isDarkmode')) {
            bodyClasslist.remove('isDarkmode');
        }

        // In embedded contexts the theme is determined explicitly at load time.
        // Set color-scheme on body so portal overlays (e.g. calendar picker) match
        // the determined theme instead of following the OS/browser preference.
        if (isInModelDrivenAppContext || isTeams) {
            document.body.style.colorScheme = isDarkmode ? 'dark' : 'light';
        }
    }, [isDarkmode]);

    useEffect(() => {
        // Mark phone mode on the body so global CSS (and body-level portals like the bottom
        // sheets) can opt out of the desktop scrollbar styling and fall back to the native
        // auto-hiding overlay scrollbar. Mirrors the isDarkmode body-class pattern above.
        const bodyClasslist = document.body.classList;
        if (!bodyClasslist) {
            return;
        }

        if (isInDesktopMode) {
            bodyClasslist.remove('isPhoneMode');
        } else {
            bodyClasslist.add('isPhoneMode');
        }
    }, [isInDesktopMode]);

    const ctx = useMemo<UiContext>(
        () => ({
            isDarkmode,
            isTeams,
            isInIframe,
            isInModelDrivenApp: isInModelDrivenAppContext,
            isInDesktopMode,
            isInPhoneMode: !isInDesktopMode,
            layout,
            orientation,
            showNavbar,
            setShowNavbar,
        }),
        [isDarkmode, isInDesktopMode, layout, orientation, showNavbar],
    );

    return <Provider value={ctx}>{children}</Provider>;
};
